import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { connect, useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';

import shippingBox from 'images/shipping-box.svg';
import { AppState } from 'types/app';
import { ProductStyle } from 'types/cloudCatalog';
import { FormattedProductBundle } from 'reducers/detail/productDetail';
import useMartyContext from 'hooks/useMartyContext';
import { onEvent } from 'helpers/EventHelpers';
import { constructMSAImageUrl } from 'helpers/index.js';
import { generateRetinaImageParams } from 'helpers/ProductUtils';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { isDesktop } from 'helpers/ClientUtils';
import { isAssigned, triggerAssignment } from 'actions/ab';
import { HYDRA_STICKY_ADD_INFO } from 'constants/hydraTests';

import css from 'styles/components/productdetail/addToCartSticky.scss';
// eslint-disable-next-line css-modules/no-unused-class
import cssAddToCart from 'styles/components/productdetail/addToCart.scss';

interface Props {
  addToCartText: string;
  isAppAdvertisementShowing: boolean;
  productDetail?: FormattedProductBundle;
  inView: boolean;
  entry: IntersectionObserverEntry | undefined;
  isDisabled: boolean | undefined;
  isSelectionValid: boolean;
  stockId: string | undefined;
  productStyle: ProductStyle;
  onHideSelectSizeTooltip: () => void;
  onShowSelectSizeTooltip: () => void;
  onAddToCart: (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>) => void;
}

/**
 * PDP Sticky Add To Cart component ids
 */
const CONFIG = {
  STICKY_PARENT: 'root', // The parent node for the sticky add to cart
  BUY_BOX_FORM: 'buyBoxForm', // The buy box form
  SIZE_SELECTION: 'sizingChooser' // The container for the sizing options
};

/**
 * PDP Sticky Add To Cart Button
 * @returns {FunctionComponent}
 */
const AddToCartSticky: FunctionComponent<Props> = ({
  inView,
  entry,
  addToCartText,
  productDetail,
  isDisabled,
  stockId,
  productStyle,
  isSelectionValid,
  isAppAdvertisementShowing,
  onAddToCart,
  onShowSelectSizeTooltip,
  onHideSelectSizeTooltip
}) => {
  const {
    preventOnTouchDevice,
    testId,
    marketplace: { pdp: pdpConfig }
  } = useMartyContext();

  // Retrieves the featured image
  const featuredImage = productStyle.images?.[0] || false;

  // Figures out pricing
  const { originalPrice, percentOff, price } = productStyle;
  const hasDiscount = percentOff !== '0%';

  const isHydraStickyAddInfo = useSelector(state => isAssigned(HYDRA_STICKY_ADD_INFO, 1, state));

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(triggerAssignment(HYDRA_STICKY_ADD_INFO));
  }, [triggerAssignment]);

  // Mounts the sticky add to cart button on the "root" div
  const addToCartStickyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const martyRoot = document.getElementById(CONFIG.STICKY_PARENT);
    const addToCartStickyNode = addToCartStickyRef.current;
    if (martyRoot && addToCartStickyNode) {
      martyRoot.appendChild(addToCartStickyNode);
    }
    return () => {
      if (martyRoot && addToCartStickyNode) {
        martyRoot.removeChild(addToCartStickyNode);
      }
    };
  }, [addToCartStickyRef]);

  // Checks upper boundary
  const [upperBoundary, setUpperBoundary] = useState(false);
  const [isPageOffset, setIsPageOffset] = useState(false);
  const checkUpperBoundary = useCallback(
    debounce(() => {
      const addToCartStickyNode = addToCartStickyRef.current;
      if (addToCartStickyNode) {
        const topDistance = window.pageYOffset + addToCartStickyNode.getBoundingClientRect().top;
        setUpperBoundary(topDistance > 0 && topDistance < 290);

        /*
          isPageOffset hasn't been set yet
          and pageYOffset is greater than 0
        */
        if (!isPageOffset && window.pageYOffset) {
          setIsPageOffset(true);
        }
      }
    }, 250),
    []
  );
  useEffect(() => {
    const scrollHandler = onEvent(window, 'scroll', checkUpperBoundary);
    const resizeHandler = onEvent(window, 'resize', checkUpperBoundary);
    return () => {
      scrollHandler.unbind();
      resizeHandler.unbind();
    };
  }, [checkUpperBoundary]);

  // Handles add to cart action
  const addToCartAction = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isSelectionValid || isDisabled) {
      // 1 - If user is missing selections, scrolls smoothly the user to it
      // 2 - If the item is auto of stock, scrolls with no animation to it (the out of stock modal actually break the animation)
      document.getElementById(CONFIG.SIZE_SELECTION)?.scrollIntoView({
        behavior: isDisabled ? 'auto' : 'smooth',
        block: 'center'
      });
    } else {
      // 3 - User is good to go, scrolls with no animation to it (the add to cart success modal actually break the animation)
      document.getElementById(CONFIG.STICKY_PARENT)?.scrollIntoView();
    }
    onAddToCart(e);
  };

  /*
    if you are desktop show button immediately
  */
  const shouldShowInitially = isDesktop() || isPageOffset;

  return (
    <div
      data-test-id={testId('stickyAddToCartBanner')}
      aria-hidden={!entry ? true : false}
      ref={addToCartStickyRef}
      className={cn(css.wrapper, {
        [css.shouldStick]: entry && shouldShowInitially && !inView && !upperBoundary,
        [css.stickToTheTop]: entry && entry?.boundingClientRect.top <= 0,
        [css.stickToTheBottom]: entry && entry?.boundingClientRect.top > 0,
        [css.appDownloadBanner]: isAppAdvertisementShowing
      })}
    >
      <div className={cn(css.container, { [css.notHydraStickyInfo]: !isHydraStickyAddInfo })}>
        {featuredImage && (
          <div className={cn(css.featuredImage, css.productInfo, { [css.notHydraStickyInfo]: !isHydraStickyAddInfo })}>
            <img
              alt={featuredImage.type}
              src={constructMSAImageUrl(featuredImage.imageId, {
                width: 93,
                height: 62,
                autoCrop: true
              })}
              srcSet={`${constructMSAImageUrl(featuredImage.imageId, generateRetinaImageParams({ width: 93, height: 62, autoCrop: true }, 2))} 2x`}
            />
          </div>
        )}
        <div
          className={cn(css.productName, css.productInfo, { [css.notHydraStickyInfo]: !isHydraStickyAddInfo })}
        >{`${productDetail?.brandName} ${productDetail?.productName}`}</div>
        {pdpConfig.showFreeShipping && (
          <div className={cn(css.shipsFree, css.productInfo, { [css.notHydraStickyInfo]: !isHydraStickyAddInfo })}>
            <img src={shippingBox} alt="Ships Free" />
            ships free
          </div>
        )}
        <div className={cn(css.price, css.productInfo, { [css.hasDiscount]: hasDiscount }, { [css.notHydraStickyInfo]: !isHydraStickyAddInfo })}>
          {price}
          {hasDiscount && <span className={css.originalPrice}>{originalPrice}</span>}
        </div>
        <div className={cn(css.addToCart, { [css.notHydraStickyInfo]: !isHydraStickyAddInfo })}>
          <button
            className={cn(cssAddToCart.cartButton, {
              [css.disabled]: isDisabled
            })}
            type="submit"
            form={CONFIG.BUY_BOX_FORM}
            data-stock-id={stockId}
            data-track-action="Product-Page"
            data-track-label="PrForm"
            data-track-value="Add-To-Cart-Sticky"
            data-test-id={testId('addToCartSticky')}
            onClick={addToCartAction}
            onMouseEnter={preventOnTouchDevice(onShowSelectSizeTooltip)}
            onMouseLeave={preventOnTouchDevice(onHideSelectSizeTooltip)}
            onFocus={preventOnTouchDevice(onShowSelectSizeTooltip)}
            onBlur={preventOnTouchDevice(onHideSelectSizeTooltip)}
          >
            {addToCartText}
          </button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  isAppAdvertisementShowing: state?.appAdvertisement?.isShowing
});

export default withErrorBoundary('AddToCartSticky', connect(mapStateToProps)(AddToCartSticky));
